import {
  AbstractType,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  InjectionToken,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChange,
  Type,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { createFormGroupFromSchema } from '@app/components/form/services/schema-form/create-form';
import { createFormDefinitionsFromSchema } from '@app/components/form/services/schema-form/create-groups';
import {
  IFieldPreferences,
  IFormAction,
  IFormGroupPreferences,
  XUX_FORM_ACTIONS,
} from '@app/components/form/services/schema-form/schema-form-interfaces';
import { ValidationService } from '@app/components/form/services/validation/validation.service';
import { DetailDrawerFormData } from '@app/components/popup/types/drawer-form.types';
import { XVII18nService } from '@app/i18n/services/xvi-translate.service';
import { XVIError } from '@app/shared/models/xvi-error.model';
import {
  BehaviorSubject,
  Observable,
  observableCombineLatest,
  observableOf,
  rx,
  Subject,
  Subscription,
} from '@app/shared/rx/rx-wrapper';
import { StateService, ToolsService } from '@app/shared/services';
import { IWidgetEvent } from '@app/shared/types/common/widget-event.type';
import { WIDGET_FAMILY } from '@config/global-constants';
import { SchemaNode } from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions/types/common.type';
import { get, head, invoke, isEmpty, isEqual, isFunction } from 'lodash';
import { GLOBAL_DND_COMPLICATE_COMP_SHOW_LOADER_OBS } from '../constants/xnx-dnd.subjects';
import { DndMode } from '../enums';
import { DndConfig, DndItem, DndItemContent, FormReadyEvent } from '../types';
import { lazyInject } from '@app/containers/base/decorators';
import { XuxDndCacheService } from '@app/shared/services/xux-dnd-cache/xux-dnd-cache.service';
import {
  IUserSetting,
  USER_CONFIG_MODULE_NAME,
  USER_CONFIG_MODULE_TYPE,
} from '@shared/types';
import { UiActions } from '@store/actions';
import { I18n } from '@app/i18n/strings-keys';

@Component({
  selector: 'xux-dnd-item',
  templateUrl: './xux-dnd-item.component.html',
  styleUrls: ['./xux-dnd-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class XuxDndItemComponent implements OnInit, OnChanges, OnDestroy {
  @lazyInject(XuxDndCacheService)
  public xuxDndCacheService: XuxDndCacheService;
  @lazyInject(ToolsService)
  public toolsService: ToolsService;
  @lazyInject(UiActions)
  public uiActions: UiActions;

  @Input()
  public itemData: DndItem;

  @Input()
  public dndConfig: DndConfig;

  public autoRefreshSwitchFields: IFieldPreferences = {};

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public get formConfig(): DndItemContent {
    return this.itemData?.content;
  }

  public data: DetailDrawerFormData;

  @Output()
  public remove: EventEmitter<DndItem> = new EventEmitter<DndItem>();

  @Output() public formAction: EventEmitter<IFormAction> = new EventEmitter();

  @Output() public formReady: EventEmitter<FormReadyEvent> = new EventEmitter();

  @Output() public formValueChanged: EventEmitter<DetailDrawerFormData> =
    new EventEmitter<DetailDrawerFormData>();

  // form configuration
  public defaultWidgetFamily: string;
  public get form(): FormGroup | undefined {
    return this.form$.getValue();
  }
  public groups: IFormGroupPreferences[] = [];

  public error$: Observable<XVIError> = observableOf(null);

  protected form$: BehaviorSubject<FormGroup | undefined> = new BehaviorSubject(
    undefined,
  );

  public get isDragModeObs(): Observable<boolean> {
    return this.dndConfig?.modeObs?.pipe(
      rx.map((mode) => mode === DndMode.DRAG),
      rx.distinctUntilChanged(),
    );
  }

  /** Notice: keep in private and use {@link emitWidgetEvent} method to dispatch events */
  protected _widgetsEventBus$: Subject<IWidgetEvent> =
    new Subject<IWidgetEvent>();
  public subscriptions: Subscription[] = [];

  public constructor(
    public cdf: ChangeDetectorRef,
    public stateService: StateService,
    public i18nService: XVII18nService,
    public injector: Injector,
  ) {}

  public ngOnChanges(
    changes: Record<keyof XuxDndItemComponent, SimpleChange>,
  ): void {
    if (changes.itemData) {
      this.prepareForm();
    }
  }

  public async ngOnInit() {
    await this.prepareForm();
    await this.initAutoRefresh();
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  public async prepareForm() {
    this.defaultWidgetFamily = get(
      this.formConfig,
      'defaultWidgetFamily',
      WIDGET_FAMILY.XUX,
    );

    await this.getFormData();

    this.initForm(get(this.formConfig, 'schema', {}) ?? {});
  }

  public beforeFormGroupChange = async (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    groupIndex: number,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    group: IFormGroupPreferences,
  ): Promise<boolean> => {
    return true;
  };

  public async getFormData(): Promise<void> {
    const formData = get(this.formConfig, 'data');

    if (!formData) {
      this.data = {};
    }

    if (isFunction(formData)) {
      this.data = await formData();
    }
  }

  public onFormAction = async (formAction: IFormAction): Promise<void> => {
    invoke(this.formConfig, 'onFormAction', formAction);
    this.formAction.emit(formAction);
  };

  public removeClick(): void {
    this.remove.emit(this.itemData);
  }

  public getShowLoaderObs() {
    return GLOBAL_DND_COMPLICATE_COMP_SHOW_LOADER_OBS.pipe(
      rx.map(
        (complicateShowLoader) => complicateShowLoader?.[this.itemData.key],
      ),
      rx.distinctUntilChanged(),
    );
  }

  protected initForm(schema: SchemaNode): void {
    const formRules = this.formConfig.formRules;
    const form = createFormGroupFromSchema({
      schema,
      rules: formRules,
      i18nService: this.i18nService,
    });

    this.form$.next(form);
    if (!this.formConfig.validation) {
      // TODO: turn off validation for deeply nested controls
      Object.values(this.form.controls || {}).forEach((control) =>
        control.clearValidators(),
      );
    } else {
      this.error$ = observableCombineLatest([
        this.form.statusChanges.pipe(rx.startWith(null)),
        this.form.valueChanges.pipe(rx.startWith(null)),
      ]).pipe(
        rx.throttleTime(250, undefined, { leading: true, trailing: true }),
        rx.map(() =>
          this.form.valid
            ? null
            : ValidationService.findFormError(this.form, formRules, {
                i18nService: this.i18nService,
              }),
        ),
        rx.distinctUntilChanged(isEqual),
      );
    }

    if (!isEmpty(this.data)) {
      this.form.patchValue(this.data);
    } else if (
      get(this.formConfig, 'cacheFormValue') &&
      this.xuxDndCacheService.getCacheValueByModuleKey(this.itemData.key)
    ) {
      this.form.patchValue(
        this.xuxDndCacheService.getCacheValueByModuleKey(this.itemData.key),
      );
    }

    this.groups = createFormDefinitionsFromSchema({
      schema,
      rules: this.formConfig.formRules,
      form: this.form,
      eventBus$: this._widgetsEventBus$.asObservable(),
      i18nService: this.i18nService,
    });

    if (isEmpty(get(this.formConfig, ['formRules', 'groups']))) {
      this.groups.forEach((group) => (group.name = ''));
    }

    setTimeout(() => {
      this.formConfig?.on?.formReady(this.form, this.groups);
      this.formReady.emit({ form: this.form, groups: this.groups });
    }, 0);

    head(this.groups).action.next({ type: XUX_FORM_ACTIONS.FORM_READY });

    this.formValueSubscription();

    this.cdf.markForCheck();
  }

  protected formValueSubscription(): void {
    this.form.valueChanges.subscribe((value) => {
      if (isFunction(this.formConfig?.on?.formValueChanged)) {
        this.formConfig?.on?.formValueChanged(value);
      }

      if (get(this.formConfig, 'cacheFormValue')) {
        const moduleKey = get(this.itemData, 'key');

        this.xuxDndCacheService.createFormValueChangeHandler(moduleKey)(value);
      }

      this.formValueChanged.emit(value);
    });
  }

  private async initAutoRefresh(): Promise<void> {
    if (!this.itemData.content?.autoRefresh?.enabled) {
      return;
    }
    const defaultToggle = this.stateService.getAutoRefreshToggle(
      this.itemData.key,
    );

    this.autoRefreshSwitchFields = {
      disabledObs: this.isDragModeObs,
      control: new FormControl(defaultToggle),
      title: this.i18nService.instant(
        I18n.InfoMessages.DashboardWidgetsAutoRefreshSwitchLabel,
        {
          autoRefreshInterval:
            this.itemData.content.autoRefresh.interval ??
            this.i18nService.instant(I18n.Common.NA),
        },
      ),
      widgetData: {
        checkedText: '',
        uncheckedText: '',
      },
    };
    const autoRefreshObs = this.itemData.content.autoRefresh.autoRefreshObs;
    const dragModeSubscribe = this.isDragModeObs.pipe().subscribe((isDrag) => {
      autoRefreshObs.next(!isDrag);
    });
    this.subscriptions.push(dragModeSubscribe);

    const switchValueSubscribe =
      this.autoRefreshSwitchFields.control.valueChanges.subscribe(
        async (value: boolean) => {
          const dashboardWidgetsConfig = this.stateService.getUserConfig(
            USER_CONFIG_MODULE_NAME.DASHBOARD_WIDGETS,
            USER_CONFIG_MODULE_TYPE.DASHBOARD,
          );
          const newDashboardWidgetsModules = {
            ...dashboardWidgetsConfig?.module_config,
            [this.itemData.key]: { auto_refresh: value },
          };
          await this.saveAutoRefreshConfig(newDashboardWidgetsModules);
          autoRefreshObs.next(value);
        },
      );

    this.subscriptions.push(switchValueSubscribe);
  }

  private async saveAutoRefreshConfig(
    newDashboardWidgetsModules: IUserSetting,
  ): Promise<void> {
    await this.toolsService
      .saveUserConfig(
        USER_CONFIG_MODULE_TYPE.DASHBOARD,
        USER_CONFIG_MODULE_NAME.DASHBOARD_WIDGETS,
        newDashboardWidgetsModules,
      )
      .toPromise();
    this.uiActions.updateUserConfig([
      {
        module_config: newDashboardWidgetsModules,
        module_type: USER_CONFIG_MODULE_TYPE.DASHBOARD,
        module_name: USER_CONFIG_MODULE_NAME.DASHBOARD_WIDGETS,
      },
    ]);
  }
}
